import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useKeycloak } from '@react-keycloak/web';

export type SeriesPoint = { name: string; value: number };

export interface ActiveAgentItem {
  key: string;
  uuidType: string;
  uuid: string;
  runUuid: string;
  jobUuid: string | null;
  agentUuid: string;
  scriptHash: string;
  status: string;
  enqueuedAt: string;
  startedAt: string;
  pid: number | null;
  agentTitle: string;
  organisationName: string;
  memoryMb: number | null;
}

export interface ActiveAgentsResponse {
  series: SeriesPoint[];
  poolSize: number;
  items: ActiveAgentItem[];
}

export function useActiveAgents(options?: any): UseQueryResult<ActiveAgentsResponse, Error> {
  const { keycloak } = useKeycloak();

  return useQuery<ActiveAgentsResponse, Error>({
    queryKey: ['active-agents'],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_AI_ENGINE_ENDPOINT}/api/v1/bundles/agent_runtime_bundle/functions/active_agents`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${keycloak.token}`,
          },
          method: 'POST',
        }
      );

      const json = await response.json();

      // Expected API-ish shape:
      // json.data.series -> array
      // json.data.poolSize (or pool_size / pool_size) -> number
      // Adjust these lines if your API uses different keys.
      const raw = json?.data ?? {};

      const poolSize: number =
        Number(raw.poolSize ?? raw.pool_size ?? raw.pool ?? raw.max ?? 0) || 0;

      const series: SeriesPoint[] = Array.isArray(raw.series)
        ? raw.series.map((s: any) => ({
            name: String(s.name ?? s.agentTitle ?? s.label ?? ''),
            value: Number(s.value ?? 0) || 0,
            jobs: Number(s.jobs ?? 0) || 0,
          }))
        : [];

      const items: ActiveAgentItem[] = Array.isArray(raw.items) ? raw.items : [];

      return { series, poolSize, items };
    },
    refetchInterval: 10000,
    ...options,
  });
}